import {
  WorkspaceDesignPackage,
  WorkspaceFilters,
  WorkspaceModel
} from "./model";

interface RawMilestone {
  committed_date?: string | null;
  // Present on the row but deliberately unread: see startDateOf.
  planned_date?: string | null;
  actual_date?: string | null;
}

interface RawListRow {
  id?: string;
  number?: string | null;
  title?: string | null;
  description?: string | null;
  workflow_status?: string | null;
  feature_workflow_instance?: {
    id?: string | null;
    workflow_status?: string | null;
    milestone_state?: string | null;
  } | null;
  milestone_state?: string | null;
  planned_next_milestone_date?: string | null;
  projected_next_milestone_date?: string | null;
  final_deadline_date?: string | null;
  // GraphQL list used `milestones`; report-engine/logs/5 uses the instance field.
  milestones?: RawMilestone[] | null;
  workflow_instance_milestones?: RawMilestone[] | null;
}

interface RawListPayload {
  design_package_list_func?: RawListRow[] | null;
}

export type WorkspaceRawInput =
  | RawListRow[]
  | RawListPayload
  | undefined
  | null;

// A package's start = its earliest COMMITTED milestone date — the same date the
// detail viz positions by (see mapMilestones), so the card order agrees with
// what the timeline draws. null when nothing is committed yet. Dates are ISO
// strings, so a lexicographic min is a chronological min.
const startDateOf = (milestones: RawListRow["milestones"]): string | null => {
  if (!Array.isArray(milestones)) return null;
  let min: string | null = null;
  for (const m of milestones) {
    const d = m?.committed_date ?? null;
    if (d && (min === null || d < min)) min = d;
  }
  return min;
};

const toDesignPackage = (row: RawListRow): WorkspaceDesignPackage => ({
  id: row.id ?? "",
  number: row.number ?? "",
  title: row.title ?? "",
  description: row.description ?? null,
  status:
    row.feature_workflow_instance?.workflow_status ??
    row.workflow_status ??
    null,
  milestoneState:
    row.feature_workflow_instance?.milestone_state ??
    row.milestone_state ??
    null,
  // Prefer projected (actuals) over planned when both are present
  nextMilestoneDate:
    row.projected_next_milestone_date ??
    row.planned_next_milestone_date ??
    null,
  finalDeadlineDate: row.final_deadline_date ?? null,
  startDate: startDateOf(row.workflow_instance_milestones ?? row.milestones)
});

type BidSearchIndex = Record<
  string,
  { bidPackages?: Array<{ number?: string | null; name?: string | null }> }
>;

function matchesQuery(
  dp: WorkspaceDesignPackage,
  q: string,
  detailsById?: BidSearchIndex
): boolean {
  if (!q) return true;
  if (dp.number.toLowerCase().includes(q) || dp.title.toLowerCase().includes(q))
    return true;
  const bids = detailsById?.[dp.id]?.bidPackages ?? [];
  return bids.some(
    (bp) =>
      (bp.number ?? "").toLowerCase().includes(q) ||
      (bp.name ?? "").toLowerCase().includes(q)
  );
}

export function applyFilters(
  model: WorkspaceModel,
  filters: WorkspaceFilters,
  detailsById?: BidSearchIndex
): WorkspaceModel {
  const q = filters.q.trim().toLowerCase();
  const designPackages = model.designPackages.filter((dp) => {
    const matchesStatus =
      filters.status.length === 0 ||
      (dp.status != null && filters.status.includes(dp.status));
    return matchesQuery(dp, q, detailsById) && matchesStatus;
  });
  return { designPackages };
}

export function toWorkspaceModel(raw: WorkspaceRawInput): WorkspaceModel {
  const rows = Array.isArray(raw) ? raw : (raw?.design_package_list_func ?? []);
  const designPackages = rows.map(toDesignPackage);
  // Default order: the soonest-starting design package first. Packages with no
  // dated milestone sort last, so a not-yet-planned package doesn't jump to the
  // top. (A stable sort keeps the backend order among equal start dates.)
  designPackages.sort((a, b) => {
    if (a.startDate === b.startDate) return 0;
    if (a.startDate === null) return 1;
    if (b.startDate === null) return -1;
    return a.startDate < b.startDate ? -1 : 1;
  });
  return { designPackages };
}
